import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { IFormUUID, IReviewsProductTypeID } from "../../../models/nominals";
import {
    IReviewsProduct,
    IReviewsProductVariantChoices,
    IWriteReviewTemplate,
} from "../../../models/reviews";
import { useAppSelector } from "../../../store/reducers";
import { WriteReviewFormCheckboxField } from "../WriteReviewFormCheckboxField";
import { WriteReviewFormDropdownField } from "../WriteReviewFormDropdownField";
import { WriteReviewFormRadioField } from "../WriteReviewFormRadioField";
import { WriteReviewFormStarsField } from "../WriteReviewFormStarsField";
import { WriteReviewFormTextArea } from "../WriteReviewFormTextArea";
import { WriteReviewFormTextField } from "../WriteReviewFormTextField";
import { NOT_SURE_VARIANT_CHOICE_SLUG } from "../constants";
import { FieldRowGroup } from "../models";
import {
    getWriteReviewFormSelectedProduct,
    getWriteReviewFormSelectedVariant,
    getWriteReviewTemplateForForm,
} from "../selectors";

const getFieldRowGroups = (): FieldRowGroup[] => {
    return [
        [["rating"]],
        [["headline"], ["comments"]],
        [["bottomline"]],
        [["name", "location" /* 'merchant_user_email'*/], ["email_collection"]],
    ];
};

const getStarsFieldTitle = (
    selectedProduct: IReviewsProduct | null,
    selectedVariant: IReviewsProductVariantChoices,
    isMultiReview?: boolean,
): string => {
    if (!selectedProduct) {
        return "Write a Review";
    }
    return interpolate(
        t`%(prefix)s %(selectedProductName)s %(separator)s %(selectedVariantName)s`,
        {
            prefix: isMultiReview ? "" : "Write a Review for your ",
            selectedProductName: `${selectedProduct.name}`,
            selectedVariantName: `${
                selectedVariant &&
                selectedVariant !== NOT_SURE_VARIANT_CHOICE_SLUG
                    ? selectedVariant.display_name
                    : ""
            }`,
            separator: isMultiReview ? ": " : "",
        },
    );
};

const buildField = (
    fieldName: string,
    formUUID: IFormUUID,
    writeReviewTemplate: IWriteReviewTemplate,
    selectedProduct: IReviewsProduct | null,
    selectedVariant: IReviewsProductVariantChoices,
    brand?: string,
    isMultiReview?: boolean,
): React.ReactNode => {
    const field = writeReviewTemplate[fieldName];
    if (!field) {
        return null;
    }
    const commonProps = {
        formUUID,
        name: fieldName,
        field: field,
        isMultiReview,
    };
    switch (field.input_type) {
        case "Stars":
            return (
                <WriteReviewFormStarsField
                    {...commonProps}
                    title={getStarsFieldTitle(
                        selectedProduct,
                        selectedVariant,
                        isMultiReview,
                    )}
                    brand={brand}
                />
            );

        case "TextBox":
            return <WriteReviewFormTextField {...commonProps} />;

        case "TextArea":
            return <WriteReviewFormTextArea {...commonProps} />;

        case "CheckboxVertical":
            return <WriteReviewFormCheckboxField {...commonProps} />;

        case "RadioHorizontal":
        case "RadioHorizontalLinked":
        case "RadioVertical":
            return <WriteReviewFormRadioField {...commonProps} />;

        case "Dropdown":
            return <WriteReviewFormDropdownField {...commonProps} />;

        default:
            console.error(
                `Unsupported review field input type: name=${fieldName}, type=${field.input_type}`,
            );
    }

    return null;
};

const buildRow = (
    row: readonly string[],
    formUUID: IFormUUID,
    writeReviewTemplate: IWriteReviewTemplate,
    selectedProduct: IReviewsProduct | null,
    selectedVariant: IReviewsProductVariantChoices,
    brand?: string,
    isMultiReview?: boolean,
): React.ReactNode => {
    return row.map((fieldName) => {
        const classes = classNames({
            "write-review-form__field": true,
            [`write-review-form__field--${fieldName}`]: true,
        });
        return (
            <div key={fieldName} className={classes}>
                {buildField(
                    fieldName,
                    formUUID,
                    writeReviewTemplate,
                    selectedProduct,
                    selectedVariant,
                    brand,
                    isMultiReview,
                )}
            </div>
        );
    });
};

export const WriteReviewFormFields = (
    props: Immutable<{
        formUUID: IFormUUID;
        displayedProductTypeIDWhitelist?: readonly IReviewsProductTypeID[];
        brand?: string;
        isMultiReview?: boolean;
    }>,
): React.ReactNode => {
    const selectedProduct = useAppSelector((rootState) =>
        getWriteReviewFormSelectedProduct(rootState.reviews, props),
    );
    const selectedVariant = useAppSelector((rootState) =>
        getWriteReviewFormSelectedVariant(rootState.reviews, props),
    );
    const writeReviewTemplate = useAppSelector(
        (rootState) =>
            getWriteReviewTemplateForForm(rootState.reviews, props).template,
    );

    const fieldRowGroups = getFieldRowGroups();

    return (
        <>
            {fieldRowGroups.map((rows, i) => (
                <div key={i} className="write-review-form__field-row-group">
                    {rows.map((row, j) => (
                        <div key={j} className="write-review-form__field-row">
                            {buildRow(
                                row,
                                props.formUUID,
                                writeReviewTemplate,
                                selectedProduct,
                                selectedVariant,
                                props.brand,
                                props.isMultiReview,
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </>
    );
};
